import { Link } from "react-router-dom";

import type { ChainSummary, RiskTier } from "@/api";

const riskColors: Record<RiskTier, string> = {
  tight: "bg-rose-100 text-rose-700",
  moderate: "bg-amber-100 text-amber-700",
  comfortable: "bg-emerald-100 text-emerald-700",
};

function fmtMin(min: number): string {
  if (min < 60) return `${Math.round(min)}m`;
  const h = min / 60;
  return h < 24 ? `${h.toFixed(1)}h` : `${(h / 24).toFixed(1)}d`;
}

/** Whole-dollar formatter with thousands separator. `null` renders as
 *  `—` so the cost column doesn't shift when TL Rates skips a chain. */
function fmtMoney(value: number | null | undefined): string {
  if (value == null) return "—";
  return `$${Math.round(value).toLocaleString()}`;
}

export interface ChainCardProps {
  chain: ChainSummary;
}

export function ChainCard({ chain }: ChainCardProps): JSX.Element {
  const pickup = new Date(chain.first_pickup_at);
  const drop = new Date(chain.last_drop_at);
  return (
    <Link
      to={`/chains/${encodeURIComponent(chain.chain_id)}`}
      className="card block p-4 hover:border-brand-500 transition-colors"
    >
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-3">
          <span className="font-mono text-xs text-slate-400">
            {chain.chain_id.slice(0, 10)}
          </span>
          <span className="text-sm font-semibold">
            {chain.origin_market} → {chain.destination_market}
          </span>
          <span className="text-xs px-1.5 py-0.5 bg-slate-100 text-slate-600 rounded">
            k={chain.k}
          </span>
          <span
            className={`text-xs px-1.5 py-0.5 rounded ${riskColors[chain.risk_tier]}`}
          >
            {chain.risk_tier}
          </span>
        </div>
        <div className="text-right">
          <div className="text-sm font-semibold text-slate-900">
            {Math.round(chain.total_miles).toLocaleString()} mi
          </div>
          <div className="text-xs text-slate-500">
            +{Math.round(chain.total_deadhead_mi)} deadhead
          </div>
        </div>
      </div>
      <div className="grid grid-cols-5 gap-3 text-xs text-slate-600">
        <div>
          <div className="text-slate-400">drive</div>
          <div className="font-medium text-slate-900">
            {fmtMin(chain.total_drive_time_min)}
          </div>
        </div>
        <div>
          <div className="text-slate-400">slack</div>
          <div className="font-medium text-slate-900">
            {fmtMin(chain.min_slack_min)}
          </div>
        </div>
        <div>
          <div className="text-slate-400">pickup</div>
          <div className="font-medium text-slate-900">
            {pickup.toLocaleDateString(undefined, { month: "short", day: "numeric" })}
          </div>
        </div>
        <div>
          <div className="text-slate-400">drop</div>
          <div className="font-medium text-slate-900">
            {drop.toLocaleDateString(undefined, { month: "short", day: "numeric" })}
          </div>
        </div>
        <div>
          {/* Est. cost — sum of TL-Rates `predicted_cost` per load.
              Replaces the legacy "Est. revenue" metric (upstream
              commit dc8120f). `—` when the chain has no estimate. */}
          <div className="text-slate-400">est. cost</div>
          <div className="font-medium text-slate-900">
            {fmtMoney(chain.total_predicted_cost)}
          </div>
        </div>
      </div>
    </Link>
  );
}
